import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { TenantRelationsApiService } from '@modules/bcm/@shared/services';
import { TenantRelation, TenantRelationWithDisable } from '@shared/models/tenant-relation';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { isString } from '@shared/functions/is-string';
import { Berth } from '@shared/models/berth';
import { ProductService } from '@modules/bcm/products/product/product.service';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Boat } from '@shared/models/boat';
import { TenantRelationFilter } from '@shared/models/mail-filter';
import { BcmDynamicPrice, BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { TranslationService } from '@core/translation/translation.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { buildTenantScopedLocalStorageKey } from '@modules/bcm/@shared/utils/tenant-scoped-storage.util';
import { BcmCapabilityService } from '@modules/bcm/capabilities/bcm-capability.service';

@Component({
    selector: 'form-widget-tenant-relation',
    templateUrl: './form-widget-tenant-relation.component.html',
    styleUrls: ['./form-widget-tenant-relation.component.scss'],
    standalone: false
})
export class FormWidgetTenantRelationComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    readonly = false;

    @Input()
    headline?: string;

    @Input()
    label?: string;

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    @Input()
    parentFormGroup: UntypedFormGroup;

    @Input()
    set prefilledTenantRelationId(value: number) {
        this._prefilledTenantRelationId = value;
    }

    get prefilledTenantRelationId(): number {
        return this._prefilledTenantRelationId;
    }

    private _prefilledTenantRelationId: number;

    @Input()
    disableConditionCallback: (selectedItems: TenantRelationWithDisable[], allItems: TenantRelationWithDisable[]) => void;

    private _selectTenantRelation: TenantRelation;

    @Input()
    set selectTenantRelation(value: TenantRelation) {
        if (value?.id || value?.id !== this._selectTenantRelation?.id) {
            this._selectTenantRelation = value;
        }
    }

    get selectTenantRelation(): TenantRelation {
        return this._selectTenantRelation;
    }

    @Input()
    selectedTenantRelations?: TenantRelationFilter | TenantRelation[];

    @Input()
    givenTenantRelations: TenantRelation[];

    /**
     * Restricts what is selectable to these relation ids — used when a dynamic product's price rules
     * only reference a specific set of relations: picking any other one could never resolve a price.
     *
     * Empty/absent means no restriction, which is what every caller outside the dynamic-price flow
     * relies on.
     */
    @Input()
    allowedTenantRelationIds: number[];

    @Input()
    personOrCompanyName: string;

    @Input()
    tenantRelationRequired = true;

    @Input()
    slimmedView = false;

    @Input()
    givenBerth: Berth;

    @Input()
    givenBoat: Boat;

    @Input()
    disableOtherRelations = false;

    @Input()
    multipleSelectable = false;

    tenantRelationFormGroup: UntypedFormGroup;

    selectedTenantRelations$ = new Subject<TenantRelation[]>();

    selectedTenantRelationPayableOption$ = this.selectedTenantRelations$
        .pipe(map(tenantRelations => {
            return tenantRelations.find(relation => !!relation.payableOption?.id)?.payableOption;
        }));

    /**
     * Sum of all product prices across selected tenant relations.
     * Prefers the backend-evaluated dynamic price (rulePrice) over the static product price
     * so the total reflects whichever rule-driven price was last evaluated.
     */
    selectedTenantRelationProductPriceTotal$ = this.selectedTenantRelations$
        .pipe(map(tenantRelations => {
            return (tenantRelations || [])
                .flatMap(tenantRelation => tenantRelation.products)
                .reduce((accumulator: number, product) => {
                    const price = product?.dynamicPrice?.rulePrice != null
                        ? product.dynamicPrice.rulePrice
                        : (product?.price || 0);
                    return accumulator + (price * (product?.quantity || 0));
                }, 0);
        }));

    loading: boolean;

    topList: TenantRelation[] = [];

    bottomList: TenantRelation[] = [];

    filteredTopList$: Observable<TenantRelation[]>;

    filteredBottomList$: Observable<TenantRelation[]>;

    selectedFilterOption: 'Volltext' | 'Beginnt mit' = 'Volltext';

    filterOptions = ['Volltext', 'Beginnt mit'];

    private _unsubscribeAll = new Subject();

    /**
     * All relations of the tenant as returned by the API. Cached so that a changed
     * allowedTenantRelationIds (e.g. another product picked in an already open dialog) can re-split
     * the lists without a second request.
     */
    private _allTenantRelations: TenantRelation[] = [];

    /** Re-emits whenever topList/bottomList were rebuilt, so the filtered streams pick them up. */
    private _listsChanged$ = new BehaviorSubject<void>(undefined);

    /**
     * Tracks the currently selected tenant relations outside the valueChanges subscription so that
     * _evaluateDynamicPrices() can access them when the re-evaluation is triggered by a parent
     * form entity change (boat/berth/winterStorage/pier/season) rather than by the relation
     * selection itself, without re-subscribing or duplicating the selection logic.
     */
    private _currentSelectedTenantRelations: TenantRelation[] = [];

    constructor(private _formBuilder: UntypedFormBuilder,
                private _formGroupDirective: FormGroupDirective,
                public tenantRelationsApiService: TenantRelationsApiService,
                private _productService: ProductService,
                private _tolgee: TolgeeService,
                private _bcmService: BcmService,
                private _bcmCapabilityService: BcmCapabilityService) {
    }

    private _selectedFilterOptionStorageKey(): string {
        return buildTenantScopedLocalStorageKey(this._bcmService.tenant?.id, 'tr_selectedFilterOption');
    }

    ngOnInit(): void {
        const storedFilterOption = localStorage.getItem(this._selectedFilterOptionStorageKey()) as 'Volltext' | 'Beginnt mit';
        if (storedFilterOption) {
            this.selectedFilterOption = storedFilterOption;
        }

        this._createForm();

        if (this._formGroupDirective?.form && !this.parentFormGroup) {
            this.parentFormGroup = this._formGroupDirective.form;
        }

        this._loadTenantRelations(this.givenTenantRelations);

        setTimeout(() => {
            this.parentFormGroup.addControl('tenantRelationForm', this.tenantRelationFormGroup);
        });

        this._watchParentFormChanges();

        if (this.headline === undefined || this.headline === null) {
            this.headline = this.multipleSelectable
                ? this._tolgee.translate('workflows.formWidgets.tenantRelation.headlinePlural')
                : this._tolgee.translate('workflows.formWidgets.tenantRelation.headline');
        }

        if (this.label === undefined || this.label === null) {
            this.label = this.multipleSelectable
                ? this._tolgee.translate('workflows.formWidgets.tenantRelation.relationLabelPlural')
                : this._tolgee.translate('workflows.formWidgets.tenantRelation.relationLabel');
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Switching to another product in an open dialog does not recreate this component when the new
        // product still has a relation criterion, so the lists have to be re-filtered in place.
        if (changes['allowedTenantRelationIds'] && !changes['allowedTenantRelationIds'].firstChange) {
            this._splitTenantRelations();

            // Clearing writes to the shared parent FormGroup, whose valueChanges make
            // ProductDynamicPriceComponent emit outputs and run detectChanges(). Doing that straight
            // from ngOnChanges re-enters the change detection pass that is still checking the parent
            // (ExpressionChangedAfterItHasBeenCheckedError), so it is deferred to the next microtask —
            // the same guard the parent applies around its own emissions.
            Promise.resolve(null).then(() => this._clearSelectionIfNotAllowed());
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
        this._listsChanged$.complete();
        this.parentFormGroup.removeControl('tenantRelationForm');
    }

    saveSelectedFilterOption(selectedFilterOption: 'Volltext' | 'Beginnt mit') {
        localStorage.setItem(this._selectedFilterOptionStorageKey(), selectedFilterOption);
    }

    removeTenantRelation(): void {
        this.tenantRelationFormGroup.get('tenantRelation').setValue(null);
        this.tenantRelationFormGroup.markAsDirty();
    }

    private _createForm(): void {
        this.tenantRelationFormGroup = this._formBuilder.group({
            tenantRelations: [
                {
                    value: this.selectTenantRelation
                        ? [this.selectTenantRelation]
                        : this.selectedTenantRelations ?? [],
                    disabled: !this.multipleSelectable,
                },
                this.tenantRelationRequired ? [U2bValidators.required(TranslationService.translate('common.validationMsg.relationshipsRequired'))] : []
            ],
            tenantRelation: [
                {
                    value: this.selectTenantRelation,
                    disabled: this.multipleSelectable,
                },
                this.tenantRelationRequired ? [U2bValidators.required(TranslationService.translate('common.validationMsg.relationshipRequired'))] : []
            ],
        });

        // Re-evaluate dynamic prices whenever the tenant relation selection changes.
        // The selected relations are stored in _currentSelectedTenantRelations so they are
        // also accessible when a boat is selected after the relation (see _watchParentFormBoatChanges).
        this.tenantRelationFormGroup.valueChanges
            .subscribe(({tenantRelation, tenantRelations}: {
                tenantRelation: TenantRelation,
                tenantRelations: TenantRelation[]
            }) => {

                const selectedTenantRelations = (tenantRelation ? [tenantRelation] : (tenantRelations || []));

                if (tenantRelation?.id) {
                    this.selectTenantRelation = tenantRelation;
                }

                this._currentSelectedTenantRelations = selectedTenantRelations;
                this._evaluateDynamicPrices();
            });
    }

    /**
     * Listens for entity selections on the parent form group that affect dynamic pricing
     * and triggers a re-evaluation whenever a relevant entity changes.
     *
     * Each form-widget-* child component registers its controls as a named sub-group on the
     * shared parent FormGroup (boatForm, berthForm, winterStorageForm, pierForm, seasonForm).
     * We emit a snapshot of every relevant id and re-evaluate only when at least one of them
     * actually changes, so unrelated form edits don't trigger extra backend calls.
     */
    private _watchParentFormChanges(): void {
        if (!this.parentFormGroup) {
            return;
        }

        this.parentFormGroup.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                map(value => ({
                    boatId:          value?.boatForm?.boat?.id ?? null,
                    berthId:         value?.berthForm?.berth?.id ?? null,
                    winterStorageId: value?.winterStorageForm?.winterStorage?.id ?? null,
                    pierId:          value?.pierForm?.pier?.id ?? null,
                    seasonId:        value?.seasonForm?.season?.id ?? null,
                })),
                distinctUntilChanged((a, b) =>
                    a.boatId === b.boatId &&
                    a.berthId === b.berthId &&
                    a.winterStorageId === b.winterStorageId &&
                    a.pierId === b.pierId &&
                    a.seasonId === b.seasonId),
                filter(ids => Object.values(ids).some(id => !!id))
            )
            .subscribe(() => {
                this._evaluateDynamicPrices();
            });
    }

    /**
     * Calls the evaluate-price-rules endpoint for every dynamic-priced product in the
     * currently selected tenant relations, then emits on selectedTenantRelations$ so the
     * template re-renders with updated prices.
     *
     * Called from two places:
     *  1. When the tenant relation selection changes (_createForm subscription).
     *  2. When any rule-relevant entity on the parent form (boat, berth, winterStorage,
     *     pier, season) changes (_watchParentFormChanges).
     *
     * Parameter-building rules:
     * - Entities are only added to `parameters` when they carry a valid id. Omitting them
     *   entirely prevents ProductService from appending `<entity>=undefined` as a literal
     *   query-string value, which would confuse the backend.
     * - boatDimensions (width/length) are passed as a separate argument so the service
     *   appends them as boatWidth / boatLength query params, which some price rules depend on.
     *
     * forkJoin ensures selectedTenantRelations$ is only emitted once all requests have
     * settled, avoiding partial renders.
     */
    private _evaluateDynamicPrices(): void {
        const selectedTenantRelations = this._currentSelectedTenantRelations;

        const parentValue = this.parentFormGroup?.value;
        const boat: Boat = this.givenBoat || parentValue?.boatForm?.boat;
        const berth = this.givenBerth || parentValue?.berthForm?.berth;
        const winterStorage = parentValue?.winterStorageForm?.winterStorage;
        const pier = parentValue?.pierForm?.pier;
        const season = parentValue?.seasonForm?.season;

        const dynamicPricesRequests$: Observable<void>[] = [];

        selectedTenantRelations.forEach(selectedTenantRelation => {
            const parameters: Record<string, any> = {tenantRelation: selectedTenantRelation};

            if (boat?.id) {
                parameters.boat = boat;
            }

            if (berth?.id) {
                parameters.berth = berth;
            }

            if (winterStorage?.id) {
                parameters.winterStorage = winterStorage;
            }

            if (pier?.id) {
                parameters.pier = pier;
            }

            if (season?.id) {
                parameters.season = season;
            }

            for (const product of (selectedTenantRelation?.products || []).filter(p => p.hasDynamicPrice)) {
                const boatDimensions = boat?.width && boat?.length
                    ? {width: boat.width, length: boat.length}
                    : undefined;

                dynamicPricesRequests$.push(
                    this._productService.evaluatePriceRule(product, parameters, product.quantity, boatDimensions)
                        .pipe(map(dynamicPrice => {
                            // Wrap in BcmDynamicPrice so the constructor computes
                            // matchingRuleEvaluations (the isTrue-filtered subset of
                            // ruleEvaluations) that the blue info box in the tenant
                            // relation dialog renders.
                            product.dynamicPrice = new BcmDynamicPrice(dynamicPrice as unknown as BcmDynamicPriceDto);
                        }))
                );
            }
        });

        if (dynamicPricesRequests$.length === 0) {
            dynamicPricesRequests$.push(of(null));
        }

        forkJoin(dynamicPricesRequests$)
            .subscribe(() => {
                this.selectedTenantRelations$.next(selectedTenantRelations);
            });
    }

    private _loadTenantRelations(givenTenantRelations: TenantRelation[] = []): void {
        this.topList = [];
        this.bottomList = [];

        this.loading = true;

        if (!this._bcmCapabilityService.shouldPrefetch('relations.selectList')) {
            this._allTenantRelations = givenTenantRelations;
            this._splitTenantRelations();
            this.loading = false;
            return;
        }

        this.tenantRelationsApiService
            .selectOptional()
            .subscribe((tenantRelations) => {
                this._allTenantRelations = tenantRelations || [];

                this._splitTenantRelations();

                if (this.prefilledTenantRelationId && tenantRelations?.length) {
                    this.tenantRelationFormGroup.patchValue({
                        tenantRelation: tenantRelations.find(tr => tr.id === this.prefilledTenantRelationId) || null
                    });
                }

                // Deliberately no _clearSelectionIfNotAllowed() here: a selection handed in by the
                // parent (an existing invoice position, a preselected relation) must survive. Wiping it
                // would blank a filled required field on a dialog opened to change something else — and
                // the parent would not even notice, since it only starts observing valueChanges 1s in.
                // Clearing is for the product *switch*, where the old price genuinely became invalid.
            })
            .add(() => this.loading = false);

        const searchTerm$ = this.tenantRelationFormGroup.get('tenantRelation').valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                startWith(''),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
            );

        // combineLatest with _listsChanged$ so a re-split (relations loaded, or another product picked)
        // reaches the template even though the search term itself did not change.
        this.filteredTopList$ = combineLatest([searchTerm$, this._listsChanged$])
            .pipe(
                map(([value]) => isString(value) ? this._filterTenantRelations(value, this.topList) : this.topList)
            );

        this.filteredBottomList$ = combineLatest([searchTerm$, this._listsChanged$])
            .pipe(
                map(([value]) => isString(value) ? this._filterTenantRelations(value, this.bottomList) : this.bottomList)
            );
    }

    /**
     * Rebuilds topList (relations of the given person/company) and bottomList (the remaining ones)
     * from the cached API result, dropping everything the current product's price rules do not
     * reference.
     */
    private _splitTenantRelations(): void {
        this.topList = [];
        this.bottomList = [];

        const givenTenantRelations = this.givenTenantRelations || [];

        for (const tenantRelation of this._allTenantRelations) {
            if (!this._isAllowed(tenantRelation)) {
                continue;
            }

            if (this.personOrCompanyName && givenTenantRelations.find(givenTenantRelation => givenTenantRelation.id === tenantRelation.id)) {
                this.topList.push(tenantRelation);
            } else {
                this.bottomList.push(tenantRelation);
            }
        }

        this._listsChanged$.next(undefined);
    }

    private _isAllowed(tenantRelation: TenantRelation): boolean {
        if (!this.allowedTenantRelationIds?.length) {
            return true;
        }

        return this.allowedTenantRelationIds.includes(tenantRelation.id);
    }

    /**
     * Drops a selection that the current criteria no longer allow — otherwise a price evaluated for the
     * previous product's relation would silently stay in the form, for a relation that is no longer
     * even visible in the list.
     */
    private _clearSelectionIfNotAllowed(): void {
        const selected: TenantRelation = this.tenantRelationFormGroup.get('tenantRelation').value;

        if (selected?.id && !this._isAllowed(selected)) {
            this._selectTenantRelation = null;
            this.tenantRelationFormGroup.get('tenantRelation').setValue(null);
        }

        // In multi mode the real selection lives in `tenantRelations` while `tenantRelation` sits
        // disabled and empty, so the single control above would never catch a stale entry.
        const selectedMultiple: TenantRelation[] = this.tenantRelationFormGroup.get('tenantRelations').value || [];
        const allowedMultiple = selectedMultiple.filter(tenantRelation => this._isAllowed(tenantRelation));

        if (allowedMultiple.length !== selectedMultiple.length) {
            this.tenantRelationFormGroup.get('tenantRelations').setValue(allowedMultiple);
        }
    }

    private _filterTenantRelations(searchTerm: string, tenantRelations: TenantRelation[]): TenantRelation[] {
        return tenantRelations.filter(tenantRelation => tenantRelation.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    public displayTenantRelationWith(tenantRelation: any): string {
        return tenantRelation?.name || '';
    }
}
